import React from "react";
import styled from "styled-components";

import Page from "../../components/Page";
import { Button, ColoredButton, DangerButton } from "../../components/buttons";
import api from "../../services/api";
import toasts from "../../services/toasts";


const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding-bottom: 40px;
`;

const Card = styled.div`
  padding: 18px;
  border: 1px solid var(--border-color);
  border-radius: 14px;
  background: var(--surface-bg);
  box-shadow: var(--button-shadow);
`;

const SafetyCard = styled(Card)`
  border-color: rgba(66, 244, 179, 0.45);
  background: rgba(66, 244, 179, 0.08);
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
`;

const Title = styled.h4`
  margin: 0 0 8px;
`;

const Note = styled.p`
  margin: 0;
  line-height: 1.5;
  color: var(--muted-text);
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 12px;
`;

const SummaryItem = styled.div`
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background: var(--app-bg);
`;

const SummaryValue = styled.div`
  margin-top: 4px;
  font-size: 22px;
  font-weight: 800;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--muted-text);
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 10px;
  border: 1px solid var(--input-border);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--text-color);
`;

const Select = styled.select`
  width: 100%;
  padding: 9px 10px;
  border: 1px solid var(--input-border);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--text-color);
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 125px;
  padding: 10px;
  border: 1px solid var(--input-border);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--text-color);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  resize: vertical;
`;

const Field = styled.div`
  min-width: 0;
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryPlanRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
`;

const CategoryPlanRow = styled.div`
  display: grid;
  grid-template-columns:
    minmax(110px, 0.8fr) minmax(150px, 1fr) 90px minmax(180px, 1.35fr)
    minmax(180px, 1.2fr) auto;
  gap: 10px;
  align-items: end;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background: var(--app-bg);

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  margin: 14px 0;
`;

const TableWrap = styled.div`
  overflow: auto;
  max-height: 620px;
  border: 1px solid var(--border-color);
  border-radius: 10px;
`;

const Table = styled.table`
  width: 100%;
  min-width: 1050px;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    padding: 10px;
    text-align: left;
    border-bottom: 1px solid var(--border-color);
    vertical-align: top;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--surface-bg);
    font-size: 11px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--muted-text);
  }

  tbody tr:hover {
    background: var(--link-hover-bg);
  }
`;

const Badge = styled.span`
  display: inline-flex;
  padding: 4px 8px;
  border-radius: 999px;
  background: ${({ status }) => {
    if (status === "published") return "rgba(66, 244, 179, 0.18)";
    if (status === "approved" || status === "enriched")
      return "rgba(74, 144, 226, 0.18)";
    if (status === "failed" || status === "rejected")
      return "rgba(216, 87, 87, 0.17)";
    if (status === "skipped_existing") return "rgba(236, 180, 68, 0.2)";
    return "rgba(127, 127, 127, 0.14)";
  }};
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
`;

const PlaceName = styled.div`
  font-weight: 800;
  margin-bottom: 3px;
`;

const Muted = styled.div`
  color: var(--muted-text);
  font-size: 12px;
  line-height: 1.4;
`;

const RunList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 14px;
`;

const RunItem = styled.div`
  display: grid;
  grid-template-columns: 90px 110px minmax(180px, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 10px;
  border: 1px solid var(--border-color);
  border-radius: 9px;
  background: var(--app-bg);

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const Empty = styled.div`
  padding: 28px 0;
  text-align: center;
  color: var(--muted-text);
`;

const getErrorMessage = error => {
  if (error && error.response && error.response.data) {
    return (
      error.response.data.message ||
      error.response.data.error ||
      error.toString()
    );
  }
  return error && error.toString ? error.toString() : "An error occurred";
};

const prettyJson = value => JSON.stringify(value || {}, null, 2);

const DEFAULT_CATEGORY_PLAN = [
  {
    key: "gyms",
    label: "Gyms",
    search_term: "best gyms",
    target_count: 6,
    place_category_id: "",
    match_terms: "gym, fitness center, health club"
  },
  {
    key: "cinemas",
    label: "Cinemas",
    search_term: "best cinemas",
    target_count: 6,
    place_category_id: "",
    match_terms: "cinema, movie theater, film theater"
  },
  {
    key: "bars",
    label: "Bars",
    search_term: "best bars",
    target_count: 6,
    place_category_id: "",
    match_terms: "bar, cocktail bar, pub, wine bar"
  },
  {
    key: "cafes",
    label: "Cafes",
    search_term: "best cafes",
    target_count: 6,
    place_category_id: "",
    match_terms: "cafe, café, coffee shop"
  },
  {
    key: "museums",
    label: "Museums",
    search_term: "best museums",
    target_count: 6,
    place_category_id: "",
    match_terms: "museum, art museum, history museum"
  }
];

const APIFY_DISCOVERY_TASK_ID = "oZl1gzRaEvgTalfMd";
const APIFY_ENRICHMENT_TASK_ID = "2wwEINmDYQEzPOaQk";

const CITY_PRESETS = [
  {
    name: "Copenhagen",
    slug: "copenhagen",
    country_code: "dk",
    timezone: "Europe/Copenhagen",
    center_lat: 55.6761,
    center_lng: 12.5683
  },
  {
    name: "Stockholm",
    slug: "stockholm",
    country_code: "se",
    timezone: "Europe/Stockholm",
    center_lat: 59.3293,
    center_lng: 18.0686
  },
  {
    name: "Oslo",
    slug: "oslo",
    country_code: "no",
    timezone: "Europe/Oslo",
    center_lat: 59.9139,
    center_lng: 10.7522
  },
  {
    name: "Vienna",
    slug: "vienna",
    country_code: "at",
    timezone: "Europe/Vienna",
    center_lat: 48.2082,
    center_lng: 16.3738
  },
  {
    name: "Prague",
    slug: "prague",
    country_code: "cz",
    timezone: "Europe/Prague",
    center_lat: 50.0755,
    center_lng: 14.4378
  },
  {
    name: "Budapest",
    slug: "budapest",
    country_code: "hu",
    timezone: "Europe/Budapest",
    center_lat: 47.4979,
    center_lng: 19.0402
  },
  {
    name: "Warsaw",
    slug: "warsaw",
    country_code: "pl",
    timezone: "Europe/Warsaw",
    center_lat: 52.2297,
    center_lng: 21.0122
  },
  {
    name: "Dublin",
    slug: "dublin",
    country_code: "ie",
    timezone: "Europe/Dublin",
    center_lat: 53.3498,
    center_lng: -6.2603
  },
  {
    name: "Zurich",
    slug: "zurich",
    country_code: "ch",
    timezone: "Europe/Zurich",
    center_lat: 47.3769,
    center_lng: 8.5417
  },
  {
    name: "Istanbul",
    slug: "istanbul",
    country_code: "tr",
    timezone: "Europe/Istanbul",
    center_lat: 41.0082,
    center_lng: 28.9784
  },
  {
    name: "Toronto",
    slug: "toronto",
    country_code: "ca",
    timezone: "America/Toronto",
    center_lat: 43.6532,
    center_lng: -79.3832
  },
  {
    name: "Los Angeles",
    slug: "los-angeles",
    country_code: "us",
    timezone: "America/Los_Angeles",
    center_lat: 34.0522,
    center_lng: -118.2437
  },
  {
    name: "San Francisco",
    slug: "san-francisco",
    country_code: "us",
    timezone: "America/Los_Angeles",
    center_lat: 37.7749,
    center_lng: -122.4194
  },
  {
    name: "Chicago",
    slug: "chicago",
    country_code: "us",
    timezone: "America/Chicago",
    center_lat: 41.8781,
    center_lng: -87.6298
  },
  {
    name: "Mexico City",
    slug: "mexico-city",
    country_code: "mx",
    timezone: "America/Mexico_City",
    center_lat: 19.4326,
    center_lng: -99.1332
  },
  {
    name: "Tokyo",
    slug: "tokyo",
    country_code: "jp",
    timezone: "Asia/Tokyo",
    center_lat: 35.6762,
    center_lng: 139.6503
  },
  {
    name: "Seoul",
    slug: "seoul",
    country_code: "kr",
    timezone: "Asia/Seoul",
    center_lat: 37.5665,
    center_lng: 126.978
  },
  {
    name: "Singapore",
    slug: "singapore",
    country_code: "sg",
    timezone: "Asia/Singapore",
    center_lat: 1.3521,
    center_lng: 103.8198
  },
  {
    name: "Bangkok",
    slug: "bangkok",
    country_code: "th",
    timezone: "Asia/Bangkok",
    center_lat: 13.7563,
    center_lng: 100.5018
  },
  {
    name: "Sydney",
    slug: "sydney",
    country_code: "au",
    timezone: "Australia/Sydney",
    center_lat: -33.8688,
    center_lng: 151.2093
  },
  {
    name: "Melbourne",
    slug: "melbourne",
    country_code: "au",
    timezone: "Australia/Melbourne",
    center_lat: -37.8136,
    center_lng: 144.9631
  },
  {
    name: "Cape Town",
    slug: "cape-town",
    country_code: "za",
    timezone: "Africa/Johannesburg",
    center_lat: -33.9249,
    center_lng: 18.4241
  }
];

const CATEGORY_NAME_HINTS = {
  gyms: ["gym", "fitness"],
  cinemas: ["cinema", "movie theater"],
  bars: ["bar", "pub", "nightlife"],
  cafes: ["cafe", "coffee"],
  museums: ["museum"]
};

const cloneDefaultCategoryPlan = () =>
  DEFAULT_CATEGORY_PLAN.map(entry => ({ ...entry }));

const blankCityForm = {
  city_preset: "",
  name: "",
  slug: "",
  country_code: "",
  timezone: "UTC",
  center_lat: "",
  center_lng: "",
  radius_meters: 10000,
  min_rating: 4,
  min_reviews: 100,
  max_candidates: 50,
  default_place_category_id: "",
  discovery_task_id: "",
  enrichment_task_id: "",
  discovery_input: prettyJson({
    searchStringsArray: ["top places in {{city_name}}"],
    locationQuery: "{{city_name}}",
    maxCrawledPlacesPerSearch: "{{max_candidates}}"
  }),
  enrichment_input: prettyJson({
    placeIds: "{{place_ids}}"
  }),
  category_plan: cloneDefaultCategoryPlan(),
  is_enabled: true
};

const newCityForm = () => ({
  ...blankCityForm,
  category_plan: cloneDefaultCategoryPlan()
});

const formFromCity = city => ({
  city_preset: CITY_PRESETS.some(preset => preset.slug === city.slug)
    ? city.slug
    : "custom",
  name: city.name || "",
  slug: city.slug || "",
  country_code: city.country_code || "",
  timezone: city.timezone || "UTC",
  center_lat: city.center_lat,
  center_lng: city.center_lng,
  radius_meters: city.radius_meters,
  min_rating: city.min_rating === null ? "" : city.min_rating,
  min_reviews: city.min_reviews === null ? "" : city.min_reviews,
  max_candidates: city.max_candidates,
  default_place_category_id: city.default_place_category_id || "",
  discovery_task_id: city.discovery_task_id || "",
  enrichment_task_id: city.enrichment_task_id || "",
  discovery_input: prettyJson(city.discovery_input),
  enrichment_input: prettyJson(city.enrichment_input),
  category_plan:
    city.category_plan && city.category_plan.length
      ? city.category_plan.map(entry => ({
          ...entry,
          place_category_id: entry.place_category_id || "",
          match_terms: Array.isArray(entry.match_terms)
            ? entry.match_terms.join(", ")
            : entry.match_terms || ""
        }))
      : cloneDefaultCategoryPlan(),
  is_enabled: city.is_enabled
});

const slugify = value =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeLookupText = value =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const mapPlanCategories = (categoryPlan, categories) =>
  categoryPlan.map(entry => {
    if (entry.place_category_id) return entry;
    const hints = CATEGORY_NAME_HINTS[entry.key] || [];
    const category = categories.find(item => {
      const name = normalizeLookupText(item.name);
      return hints.some(hint => name.includes(hint));
    });
    return {
      ...entry,
      place_category_id: category ? category.id : ""
    };
  });

const formatDate = value => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

class CityIngestion extends React.Component {
  state = {
    loading: true,
    busy: null,
    photoProgress: null,
    dashboard: null,
    cityId: null,
    selectedIds: {},
    bulkCategoryId: "",
    statusFilter: "",
    showCityForm: false,
    editingCityId: null,
    cityForm: newCityForm(),
    showTestImport: false,
    testImportKind: "discovery",
    testImportJson: "[]"
  };

  componentDidMount() {
    this.load();
  }

  load = async (cityId = this.state.cityId) => {
    this.setState({ loading: true });
    try {
      const dashboard = await api.place_ingestion.dashboard({
        city_id: cityId || undefined,
        status: this.state.statusFilter || undefined
      });
      this.setState({
        dashboard,
        cityId: dashboard.selected_city_id,
        loading: false,
        selectedIds: {}
      });
    } catch (error) {
      this.setState({ loading: false });
      toasts.error(getErrorMessage(error));
    }
  };

  perform = async (key, operation, successMessage) => {
    this.setState({ busy: key });
    try {
      const result = await operation();
      toasts.success(
        typeof successMessage === "function"
          ? successMessage(result)
          : successMessage
      );
      await this.load();
      return result;
    } catch (error) {
      toasts.error(getErrorMessage(error));
      return null;
    } finally {
      this.setState({ busy: null });
    }
  };

  selectedCandidateIds = () =>
    Object.keys(this.state.selectedIds)
      .filter(id => this.state.selectedIds[id])
      .map(Number);

  toggleCandidate = candidateId => {
    this.setState(state => ({
      selectedIds: {
        ...state.selectedIds,
        [candidateId]: !state.selectedIds[candidateId]
      }
    }));
  };

  toggleAll = () => {
    const candidates =
      (this.state.dashboard && this.state.dashboard.candidates) || [];
    const allSelected =
      candidates.length > 0 &&
      candidates.every(candidate => this.state.selectedIds[candidate.id]);
    const selectedIds = {};
    candidates.forEach(candidate => {
      selectedIds[candidate.id] = !allSelected;
    });
    this.setState({ selectedIds });
  };

  openCreateCity = () => {
    this.setState({
      showCityForm: true,
      editingCityId: null,
      cityForm: newCityForm()
    });
  };

  openEditCity = city => {
    const hasPreset = CITY_PRESETS.some(preset => preset.slug === city.slug);
    const needsPresetDefaults =
      hasPreset &&
      (!city.discovery_task_id ||
        !city.enrichment_task_id ||
        (!city.center_lat && !city.center_lng));
    this.setState({
      showCityForm: true,
      editingCityId: city.id,
      cityForm: formFromCity(city)
    }, () => {
      if (needsPresetDefaults) {
        this.applyCityPreset(city.slug);
      }
    });
  };

  setCityField = (key, value) => {
    this.setState(state => ({
      cityForm: {
        ...state.cityForm,
        [key]: value
      }
    }));
  };

  applyCityPreset = presetSlug => {
    if (presetSlug === "custom") {
      this.setCityField("city_preset", "custom");
      return;
    }
    const preset = CITY_PRESETS.find(city => city.slug === presetSlug);
    if (!preset) {
      this.setCityField("city_preset", "");
      return;
    }
    const categories =
      (this.state.dashboard && this.state.dashboard.categories) || [];
    this.setState(state => ({
      cityForm: {
        ...state.cityForm,
        city_preset: preset.slug,
        name: preset.name,
        slug: preset.slug,
        country_code: preset.country_code,
        timezone: preset.timezone,
        center_lat: preset.center_lat,
        center_lng: preset.center_lng,
        radius_meters: 10000,
        min_rating: 4.2,
        min_reviews: 100,
        max_candidates: 50,
        discovery_task_id: APIFY_DISCOVERY_TASK_ID,
        enrichment_task_id: APIFY_ENRICHMENT_TASK_ID,
        category_plan: mapPlanCategories(
          state.cityForm.category_plan.length
            ? state.cityForm.category_plan
            : cloneDefaultCategoryPlan(),
          categories
        )
      }
    }));
  };

  setCategoryPlanField = (index, key, value) => {
    this.setState(state => ({
      cityForm: {
        ...state.cityForm,
        category_plan: state.cityForm.category_plan.map((entry, entryIndex) =>
          entryIndex === index ? { ...entry, [key]: value } : entry
        )
      }
    }));
  };

  addCategoryPlanRow = () => {
    this.setState(state => ({
      cityForm: {
        ...state.cityForm,
        category_plan: [
          ...state.cityForm.category_plan,
          {
            key: "",
            label: "",
            search_term: "",
            target_count: 1,
            place_category_id: "",
            match_terms: ""
          }
        ]
      }
    }));
  };

  removeCategoryPlanRow = index => {
    this.setState(state => ({
      cityForm: {
        ...state.cityForm,
        category_plan: state.cityForm.category_plan.filter(
          (entry, entryIndex) => entryIndex !== index
        )
      }
    }));
  };

  saveCity = async () => {
    const { cityForm, editingCityId } = this.state;
    const cityValues = { ...cityForm };
    delete cityValues.city_preset;
    let discoveryInput;
    let enrichmentInput;
    try {
      discoveryInput = JSON.parse(cityForm.discovery_input || "{}");
      enrichmentInput = JSON.parse(cityForm.enrichment_input || "{}");
    } catch (error) {
      toasts.error("Apify task inputs must be valid JSON objects.");
      return;
    }
    if (
      !discoveryInput ||
      Array.isArray(discoveryInput) ||
      !enrichmentInput ||
      Array.isArray(enrichmentInput)
    ) {
      toasts.error("Apify task inputs must be JSON objects.");
      return;
    }

    const payload = {
      ...cityValues,
      center_lat: Number(cityForm.center_lat),
      center_lng: Number(cityForm.center_lng),
      radius_meters: Number(cityForm.radius_meters),
      max_candidates: Number(cityForm.max_candidates),
      min_rating:
        cityForm.min_rating === "" ? null : Number(cityForm.min_rating),
      min_reviews:
        cityForm.min_reviews === "" ? null : Number(cityForm.min_reviews),
      default_place_category_id:
        cityForm.default_place_category_id === ""
          ? null
          : Number(cityForm.default_place_category_id),
      category_plan: cityForm.category_plan.map(entry => ({
        key: slugify(entry.key || entry.label || entry.search_term),
        label: String(entry.label || "").trim(),
        search_term: String(entry.search_term || "").trim(),
        target_count: Number(entry.target_count),
        place_category_id:
          entry.place_category_id === ""
            ? null
            : Number(entry.place_category_id),
        match_terms: String(entry.match_terms || "")
          .split(",")
          .map(value => value.trim())
          .filter(Boolean)
      })),
      discovery_input: discoveryInput,
      enrichment_input: enrichmentInput
    };

    const result = await this.perform(
      "save-city",
      () =>
        editingCityId
          ? api.place_ingestion.cities.update({ id: editingCityId, ...payload })
          : api.place_ingestion.cities.create(payload),
      editingCityId ? "City configuration updated." : "Testing city created."
    );
    if (result) {
      this.setState({ showCityForm: false, editingCityId: null });
    }
  };

  selectTopMix = city => {
    const targetTotal = (city.category_plan || []).reduce(
      (total, entry) => total + Number(entry.target_count || 0),
      0
    );
    if (
      !window.confirm(
        `Select the top ${targetTotal} category mix? This recalculates approvals for unpublished staging candidates only. Existing app places remain unchanged.`
      )
    ) {
      return;
    }
    this.perform(
      "select-top-mix",
      () => api.place_ingestion.cities.selectTopMix({ id: city.id }),
      result =>
        `${result.selected_total}/${result.target_total} selected (${result.backfilled} backfilled, ${result.skipped_existing} existing skipped).`
    );
  };

  runSelectionAction = action => {
    const candidateIds = this.selectedCandidateIds();
    if (!candidateIds.length) {
      toasts.error("Select at least one candidate.");
      return;
    }
    const payload = {
      candidate_ids: candidateIds,
      action
    };
    if (action === "approve" && this.state.bulkCategoryId) {
      payload.place_category_id = Number(this.state.bulkCategoryId);
    }
    this.perform(
      `selection-${action}`,
      () => api.place_ingestion.candidates.select(payload),
      result => `${result.updated} candidate(s) ${action}d.`
    );
  };

  publishSelected = () => {
    const candidateIds = this.selectedCandidateIds();
    if (!candidateIds.length) {
      toasts.error("Select at least one approved candidate.");
      return;
    }
    if (
      !window.confirm(
        "Publish selected candidates? Existing Place IDs will be skipped and left unchanged."
      )
    ) {
      return;
    }
    this.perform(
      "publish",
      () => api.place_ingestion.candidates.publish(candidateIds),
      result =>
        `${result.created} new place(s) published; ${result.skipped_existing} existing place(s) skipped.`
    );
  };

  importSelectedPhotos = async () => {
    const candidateIds = this.selectedCandidateIds();
    if (!candidateIds.length) {
      toasts.error("Select at least one published place.");
      return;
    }

    let uploaded = 0;
    let failed = 0;
    let remaining = null;
    let exhausted = 0;
    let active = 0;
    this.setState({
      busy: "photos",
      photoProgress: { uploaded, failed, remaining }
    });
    try {
      for (let batch = 0; batch < 250; batch += 1) {
        const result = await api.place_ingestion.candidates.importPhotos(
          candidateIds,
          6,
          3
        );
        uploaded += result.uploaded || 0;
        failed += result.failed || 0;
        remaining = result.remaining || 0;
        exhausted = result.exhausted || 0;
        active = result.active || 0;
        this.setState({
          photoProgress: { uploaded, failed, remaining }
        });
        if (!result.selected || remaining === 0) break;
      }
      const followUp =
        active || exhausted
          ? ` ${active} still processing and ${exhausted} exhausted retries; the button can safely be used again.`
          : "";
      toasts.success(
        `${uploaded} photo(s) uploaded in small batches; ${failed} attempt(s) failed.${followUp}`
      );
      await this.load();
    } catch (error) {
      toasts.error(
        `${getErrorMessage(error)} ${uploaded} photo(s) finished before the interruption; retrying will resume safely.`
      );
      await this.load();
    } finally {
      this.setState({ busy: null, photoProgress: null });
    }
  };

  importTestDataset = () => {
    let items;
    try {
      items = JSON.parse(this.state.testImportJson);
    } catch (error) {
      toasts.error("The test dataset must be valid JSON.");
      return;
    }
    if (!Array.isArray(items)) {
      toasts.error("The test dataset must be a JSON array.");
      return;
    }
    this.perform(
      "test-import",
      () =>
        api.place_ingestion.importDataset({
          city_id: this.state.cityId,
          kind: this.state.testImportKind,
          items
        }),
      result =>
        `Imported ${result.result_summary.received || 0} test dataset item(s).`
    );
  };

  renderCityForm() {
    const { cityForm, editingCityId, dashboard } = this.state;
    const categories = (dashboard && dashboard.categories) || [];
    const categoryTargetTotal = cityForm.category_plan.reduce(
      (total, entry) => total + Number(entry.target_count || 0),
      0
    );
    const mappedCategoryCount = cityForm.category_plan.filter(
      entry => entry.place_category_id
    ).length;
    if (!this.state.showCityForm) return null;

    return (
      <Card>
        <HeaderRow>
          <div>
            <Title>{editingCityId ? "Edit testing city" : "Add testing city"}</Title>
            <Note>
              This configuration is isolated from the existing hardcoded city
              sidebar and app inventory.
            </Note>
          </div>
          <Button onClick={() => this.setState({ showCityForm: false })}>
            Close
          </Button>
        </HeaderRow>

        <Grid style={{ marginTop: 16 }}>
          <Field>
            <Label>Choose a city</Label>
            <Select
              value={cityForm.city_preset}
              onChange={event => this.applyCityPreset(event.target.value)}
            >
              <option value="">Select a city…</option>
              {CITY_PRESETS.map(city => (
                <option key={city.slug} value={city.slug}>
                  {city.name}
                </option>
              ))}
              <option value="custom">Another city (advanced)</option>
            </Select>
          </Field>
          <SummaryItem>
            <Muted>Ready-to-run setup</Muted>
            <strong>
              {cityForm.name
                ? `${cityForm.name} · ${categoryTargetTotal} places · ${mappedCategoryCount}/${cityForm.category_plan.length} categories mapped`
                : "Select a city to fill everything automatically"}
            </strong>
          </SummaryItem>
        </Grid>

        <details
          style={{
            marginTop: 16,
            padding: 14,
            border: "1px solid var(--border-color)",
            borderRadius: 10
          }}
        >
          <summary style={{ cursor: "pointer", fontWeight: 800 }}>
            Advanced settings
          </summary>
          <Muted style={{ marginTop: 8 }}>
            Coordinates, filters, category quotas and Apify task settings are
            filled automatically. Open this only when you want to customise
            them.
          </Muted>

        <Grid style={{ marginTop: 16 }}>
          <Field>
            <Label>City name</Label>
            <Input
              value={cityForm.name}
              onChange={event => this.setCityField("name", event.target.value)}
              placeholder="Copenhagen"
            />
          </Field>
          <Field>
            <Label>Slug</Label>
            <Input
              value={cityForm.slug}
              onChange={event => this.setCityField("slug", event.target.value)}
              placeholder="copenhagen"
            />
          </Field>
          <Field>
            <Label>Country code</Label>
            <Input
              value={cityForm.country_code}
              onChange={event =>
                this.setCityField("country_code", event.target.value)
              }
              placeholder="dk"
            />
          </Field>
          <Field>
            <Label>Timezone</Label>
            <Input
              value={cityForm.timezone}
              onChange={event =>
                this.setCityField("timezone", event.target.value)
              }
              placeholder="Europe/Copenhagen"
            />
          </Field>
          <Field>
            <Label>Centre latitude</Label>
            <Input
              type="number"
              step="any"
              value={cityForm.center_lat}
              onChange={event =>
                this.setCityField("center_lat", event.target.value)
              }
            />
          </Field>
          <Field>
            <Label>Centre longitude</Label>
            <Input
              type="number"
              step="any"
              value={cityForm.center_lng}
              onChange={event =>
                this.setCityField("center_lng", event.target.value)
              }
            />
          </Field>
          <Field>
            <Label>Discovery radius (metres)</Label>
            <Input
              type="number"
              value={cityForm.radius_meters}
              onChange={event =>
                this.setCityField("radius_meters", event.target.value)
              }
            />
          </Field>
          <Field>
            <Label>Maximum candidates</Label>
            <Input
              type="number"
              value={cityForm.max_candidates}
              onChange={event =>
                this.setCityField("max_candidates", event.target.value)
              }
            />
          </Field>
          <Field>
            <Label>Minimum rating</Label>
            <Input
              type="number"
              step="0.1"
              value={cityForm.min_rating}
              onChange={event =>
                this.setCityField("min_rating", event.target.value)
              }
            />
          </Field>
          <Field>
            <Label>Minimum review count</Label>
            <Input
              type="number"
              value={cityForm.min_reviews}
              onChange={event =>
                this.setCityField("min_reviews", event.target.value)
              }
            />
          </Field>
          <Field>
            <Label>Default Playground category</Label>
            <Select
              value={cityForm.default_place_category_id}
              onChange={event =>
                this.setCityField(
                  "default_place_category_id",
                  event.target.value
                )
              }
            >
              <option value="">Choose during review</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label>Enabled</Label>
            <Select
              value={cityForm.is_enabled ? "yes" : "no"}
              onChange={event =>
                this.setCityField("is_enabled", event.target.value === "yes")
              }
            >
              <option value="yes">Enabled</option>
              <option value="no">Disabled</option>
            </Select>
          </Field>
          <Field>
            <Label>Apify discovery task ID</Label>
            <Input
              value={cityForm.discovery_task_id}
              onChange={event =>
                this.setCityField("discovery_task_id", event.target.value)
              }
              placeholder="username~task-name or task ID"
            />
          </Field>
          <Field>
            <Label>Apify enrichment task ID</Label>
            <Input
              value={cityForm.enrichment_task_id}
              onChange={event =>
                this.setCityField("enrichment_task_id", event.target.value)
              }
              placeholder="username~task-name or task ID"
            />
          </Field>
        </Grid>

        <div style={{ marginTop: 20 }}>
          <HeaderRow>
            <div>
              <Title>Automatic category mix</Title>
              <Note>
                Discovery oversamples every search term. Selection takes each
                category quota first, then backfills shortages by score.
              </Note>
            </div>
            <ButtonRow>
              <Badge status="approved">
                {categoryTargetTotal} target places
              </Badge>
              <Button onClick={this.addCategoryPlanRow}>Add category</Button>
            </ButtonRow>
          </HeaderRow>
          <CategoryPlanRows>
            {cityForm.category_plan.map((entry, index) => (
              <CategoryPlanRow key={`${entry.key || "category"}-${index}`}>
                <Field>
                  <Label>Label</Label>
                  <Input
                    value={entry.label}
                    onChange={event =>
                      this.setCategoryPlanField(
                        index,
                        "label",
                        event.target.value
                      )
                    }
                    placeholder="Gyms"
                  />
                </Field>
                <Field>
                  <Label>Apify search</Label>
                  <Input
                    value={entry.search_term}
                    onChange={event =>
                      this.setCategoryPlanField(
                        index,
                        "search_term",
                        event.target.value
                      )
                    }
                    placeholder="best gyms"
                  />
                </Field>
                <Field>
                  <Label>Target</Label>
                  <Input
                    type="number"
                    min="1"
                    value={entry.target_count}
                    onChange={event =>
                      this.setCategoryPlanField(
                        index,
                        "target_count",
                        event.target.value
                      )
                    }
                  />
                </Field>
                <Field>
                  <Label>Category match terms</Label>
                  <Input
                    value={entry.match_terms}
                    onChange={event =>
                      this.setCategoryPlanField(
                        index,
                        "match_terms",
                        event.target.value
                      )
                    }
                    placeholder="gym, fitness center"
                  />
                </Field>
                <Field>
                  <Label>Playground category</Label>
                  <Select
                    value={entry.place_category_id}
                    onChange={event =>
                      this.setCategoryPlanField(
                        index,
                        "place_category_id",
                        event.target.value
                      )
                    }
                  >
                    <option value="">Choose before auto-select</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <DangerButton
                  onClick={() => this.removeCategoryPlanRow(index)}
                  disabled={cityForm.category_plan.length === 1}
                >
                  Remove
                </DangerButton>
              </CategoryPlanRow>
            ))}
          </CategoryPlanRows>
          <Muted style={{ marginTop: 8 }}>
            The total target must not exceed maximum candidates. With the
            default 50 candidates and five searches, discovery requests up to
            10 candidates per category.
          </Muted>
        </div>

        <TwoColumn style={{ marginTop: 14 }}>
          <Field>
            <Label>Discovery task input</Label>
            <Textarea
              value={cityForm.discovery_input}
              onChange={event =>
                this.setCityField("discovery_input", event.target.value)
              }
            />
          </Field>
          <Field>
            <Label>Enrichment task input</Label>
            <Textarea
              value={cityForm.enrichment_input}
              onChange={event =>
                this.setCityField("enrichment_input", event.target.value)
              }
            />
          </Field>
        </TwoColumn>
        <Muted style={{ marginTop: 8 }}>
          Template variables: {"{{city_name}}"}, {"{{city_slug}}"},{" "}
          {"{{lat}}"}, {"{{lng}}"}, {"{{radius_meters}}"},{" "}
          {"{{max_candidates}}"} and {"{{place_ids}}"}.
        </Muted>
        </details>
        <ButtonRow style={{ marginTop: 16 }}>
          <ColoredButton
            onClick={this.saveCity}
            disabled={!cityForm.name || this.state.busy === "save-city"}
          >
            {editingCityId ? "Save city" : "Create testing city"}
          </ColoredButton>
        </ButtonRow>
      </Card>
    );
  }

  renderCandidateTable() {
    const { dashboard, selectedIds, bulkCategoryId } = this.state;
    const candidates = dashboard.candidates || [];
    const categories = dashboard.categories || [];
    const selectedCount = this.selectedCandidateIds().length;
    const selectedCity = (dashboard.cities || []).find(
      city => city.id === dashboard.selected_city_id
    );
    const categoryLabels = ((selectedCity && selectedCity.category_plan) || [])
      .reduce(
        (result, entry) => ({ ...result, [entry.key]: entry.label }),
        {}
      );

    return (
      <Card>
        <HeaderRow>
          <div>
            <Title>Candidate queue</Title>
            <Note>
              Nothing here reaches the app until you approve and explicitly
              publish it. Score combines rating and review count; rating can
              contribute 100 points and review count up to 40.
            </Note>
          </div>
          <Badge status="approved">{selectedCount} selected</Badge>
        </HeaderRow>

        <Toolbar>
          <Field style={{ minWidth: 210 }}>
            <Label>Category for approval</Label>
            <Select
              value={bulkCategoryId}
              onChange={event =>
                this.setState({ bulkCategoryId: event.target.value })
              }
            >
              <option value="">Keep city/candidate category</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </Field>
          <ColoredButton
            onClick={() => this.runSelectionAction("approve")}
            disabled={!selectedCount || this.state.busy}
          >
            Approve
          </ColoredButton>
          <DangerButton
            onClick={() => this.runSelectionAction("reject")}
            disabled={!selectedCount || this.state.busy}
          >
            Reject
          </DangerButton>
          <Button
            onClick={() =>
              this.perform(
                "content",
                () =>
                  api.place_ingestion.candidates.prepareContent(
                    this.selectedCandidateIds()
                  ),
                result => `${result.prepared} content draft(s) prepared.`
              )
            }
            disabled={!selectedCount || this.state.busy}
          >
            Prepare content
          </Button>
          <ColoredButton
            onClick={this.publishSelected}
            disabled={!selectedCount || this.state.busy}
          >
            Publish new places
          </ColoredButton>
          <Button
            onClick={this.importSelectedPhotos}
            disabled={!selectedCount || this.state.busy}
          >
            {this.state.busy === "photos" && this.state.photoProgress
              ? `Importing photos… ${this.state.photoProgress.uploaded} uploaded`
              : "Import / retry profile + 5 photos"}
          </Button>
          <Button
            onClick={() =>
              this.perform(
                "verify",
                () =>
                  api.place_ingestion.candidates.verify(
                    this.selectedCandidateIds()
                  ),
                result =>
                  `${result.ready} place(s) ready; ${result.incomplete} incomplete.`
              )
            }
            disabled={!selectedCount || this.state.busy}
          >
            Verify publication
          </Button>
        </Toolbar>

        {!candidates.length ? (
          <Empty>No candidates yet. Run discovery or import a test dataset.</Empty>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      onChange={this.toggleAll}
                      checked={
                        candidates.length > 0 &&
                        candidates.every(candidate => selectedIds[candidate.id])
                      }
                    />
                  </th>
                  <th>Place</th>
                  <th>Status</th>
                  <th>Score</th>
                  <th>Rating</th>
                  <th>Category</th>
                  <th>Photos</th>
                  <th>Canonical record</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map(candidate => (
                  <tr key={candidate.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!selectedIds[candidate.id]}
                        onChange={() => this.toggleCandidate(candidate.id)}
                      />
                    </td>
                    <td>
                      <PlaceName>{candidate.name}</PlaceName>
                      <Muted>{candidate.address || "No address"}</Muted>
                      <Muted>{candidate.external_place_id}</Muted>
                    </td>
                    <td>
                      <Badge status={candidate.status}>
                        {candidate.status.replace(/_/g, " ")}
                      </Badge>
                      {candidate.failure_message ? (
                        <Muted style={{ marginTop: 5 }}>
                          {candidate.failure_message}
                        </Muted>
                      ) : null}
                    </td>
                    <td>{candidate.score === null ? "—" : candidate.score}</td>
                    <td>
                      {candidate.rating === null ? "—" : candidate.rating}
                      <Muted>
                        {candidate.review_count === null
                          ? "No review count"
                          : `${candidate.review_count.toLocaleString()} reviews`}
                      </Muted>
                    </td>
                    <td>
                      {candidate.category_name || "Unmapped"}
                      <Muted>
                        Playground:{" "}
                        {candidate.place_category_id
                          ? categories.find(
                              category =>
                                category.id === candidate.place_category_id
                            )
                            ? categories.find(
                                category =>
                                  category.id === candidate.place_category_id
                              ).name
                            : `#${candidate.place_category_id}`
                          : "not chosen"}
                      </Muted>
                      {candidate.selection_category_key ? (
                        <Muted>
                          Mix:{" "}
                          {categoryLabels[candidate.selection_category_key] ||
                            candidate.selection_category_key}
                        </Muted>
                      ) : null}
                    </td>
                    <td>
                      {candidate.photo_counts.uploaded}/
                      {candidate.photo_counts.total} uploaded
                      {candidate.photo_counts.failed ? (
                        <Muted>
                          {candidate.photo_counts.failed} failed
                        </Muted>
                      ) : null}
                    </td>
                    <td>
                      {candidate.canonical_place_id ? (
                        <React.Fragment>
                          Place #{candidate.canonical_place_id}
                          <Muted>
                            {candidate.status === "skipped_existing"
                              ? "Existing and unchanged"
                              : "Created by ingestion"}
                          </Muted>
                          {candidate.verification_result ? (
                            <Muted>
                              {candidate.verification_result.ready
                                ? "Verified ready"
                                : `${candidate.verification_result.issues.length} verification issue(s)`}
                            </Muted>
                          ) : null}
                        </React.Fragment>
                      ) : (
                        "Not published"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
    );
  }

  render() {
    const { dashboard, loading, cityId, busy } = this.state;
    if (loading && !dashboard) {
      return <Page title="City ingestion">Loading…</Page>;
    }

    const cities = (dashboard && dashboard.cities) || [];
    const selectedCity = cities.find(city => city.id === cityId);
    const summary = (dashboard && dashboard.summary) || {};
    const runs = (dashboard && dashboard.runs) || [];
    const selectedIds = this.selectedCandidateIds();

    return (
      <Page title="City ingestion">
        <Stack>
          <SafetyCard>
            <Title>Protected testing workflow</Title>
            <Note>
              This tool never updates or deletes an existing place. When a
              scraped Place ID already exists, it is linked as{" "}
              <strong>skipped existing</strong> and the canonical record is
              left untouched. Automated photos are restricted to places
              created by this ingestion workflow.
            </Note>
          </SafetyCard>

          <Card>
            <HeaderRow>
              <div>
                <Title>Testing cities</Title>
                <Note>
                  Add cities without changing the existing sidebar or existing
                  place inventory.
                </Note>
              </div>
              <ButtonRow>
                <ColoredButton onClick={this.openCreateCity}>
                  Add testing city
                </ColoredButton>
                {selectedCity ? (
                  <Button onClick={() => this.openEditCity(selectedCity)}>
                    Edit city
                  </Button>
                ) : null}
              </ButtonRow>
            </HeaderRow>

            <Toolbar>
              <Field style={{ minWidth: 240 }}>
                <Label>City</Label>
                <Select
                  value={cityId || ""}
                  onChange={event => {
                    const nextCityId = Number(event.target.value);
                    this.setState({ cityId: nextCityId }, () =>
                      this.load(nextCityId)
                    );
                  }}
                >
                  {!cities.length ? (
                    <option value="">No testing cities</option>
                  ) : null}
                  {cities.map(city => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                      {city.is_enabled ? "" : " (disabled)"}
                    </option>
                  ))}
                </Select>
              </Field>
              <ColoredButton
                disabled={!selectedCity || !!busy}
                onClick={() =>
                  this.perform(
                    "discover",
                    () =>
                      api.place_ingestion.cities.discover({
                        id: selectedCity.id
                      }),
                    "Apify discovery started."
                  )
                }
              >
                Discover places
              </ColoredButton>
              <Button
                disabled={
                  !selectedCity ||
                  !selectedCity.category_plan ||
                  !selectedCity.category_plan.length ||
                  !!busy
                }
                onClick={() => this.selectTopMix(selectedCity)}
              >
                Select top{" "}
                {selectedCity
                  ? (selectedCity.category_plan || []).reduce(
                      (total, entry) =>
                        total + Number(entry.target_count || 0),
                      0
                    )
                  : 0}{" "}
                mix
              </Button>
              <Button
                disabled={!selectedCity || !!busy}
                onClick={() =>
                  this.perform(
                    "enrich",
                    () =>
                      api.place_ingestion.cities.enrich({
                        id: selectedCity.id,
                        candidate_ids: selectedIds.length
                          ? selectedIds
                          : undefined
                      }),
                    "Apify enrichment started."
                  )
                }
              >
                Enrich approved
              </Button>
              <Button onClick={() => this.load()} disabled={!!busy}>
                Refresh
              </Button>
              <Button
                onClick={() =>
                  this.setState(state => ({
                    showTestImport: !state.showTestImport
                  }))
                }
                disabled={!selectedCity}
              >
                Import test JSON
              </Button>
            </Toolbar>

            {selectedCity ? (
              <Muted>
                {selectedCity.center_lat}, {selectedCity.center_lng} ·{" "}
                {selectedCity.radius_meters.toLocaleString()} m · discovery
                task: {selectedCity.discovery_task_id || "not configured"} ·{" "}
                {(selectedCity.category_plan || []).reduce(
                  (total, entry) => total + Number(entry.target_count || 0),
                  0
                )}{" "}
                target places across{" "}
                {(selectedCity.category_plan || []).length} categories ·
                Apify token:{" "}
                {dashboard.apify_configured ? "configured" : "not configured"}
              </Muted>
            ) : null}
          </Card>

          {this.renderCityForm()}

          {this.state.showTestImport ? (
            <Card>
              <Title>Import an Apify-shaped test dataset</Title>
              <Note>
                Use this to validate ranking, selection and publication without
                starting a paid Actor run.
              </Note>
              <Toolbar>
                <Field style={{ minWidth: 200 }}>
                  <Label>Dataset kind</Label>
                  <Select
                    value={this.state.testImportKind}
                    onChange={event =>
                      this.setState({ testImportKind: event.target.value })
                    }
                  >
                    <option value="discovery">Discovery</option>
                    <option value="enrichment">Enrichment</option>
                  </Select>
                </Field>
              </Toolbar>
              <Textarea
                value={this.state.testImportJson}
                onChange={event =>
                  this.setState({ testImportJson: event.target.value })
                }
                style={{ minHeight: 220 }}
              />
              <ButtonRow style={{ marginTop: 12 }}>
                <ColoredButton
                  onClick={this.importTestDataset}
                  disabled={busy === "test-import"}
                >
                  Import dataset
                </ColoredButton>
              </ButtonRow>
            </Card>
          ) : null}

          {selectedCity ? (
            <React.Fragment>
              <Card>
                <Title>Pipeline summary</Title>
                <Grid>
                  {[
                    "total",
                    "discovered",
                    "filtered",
                    "approved",
                    "enrichment_running",
                    "enriched",
                    "published",
                    "skipped_existing",
                    "failed"
                  ].map(key => (
                    <SummaryItem key={key}>
                      <Muted>{key.replace(/_/g, " ")}</Muted>
                      <SummaryValue>{summary[key] || 0}</SummaryValue>
                    </SummaryItem>
                  ))}
                </Grid>
              </Card>

              {this.renderCandidateTable()}

              <Card>
                <Title>Apify runs</Title>
                <Note>
                  Sync a completed run to fetch its default dataset and update
                  the candidate queue.
                </Note>
                {!runs.length ? (
                  <Empty>No runs for this city.</Empty>
                ) : (
                  <RunList>
                    {runs.map(run => (
                      <RunItem key={run.id}>
                        <strong>{run.kind}</strong>
                        <Badge status={run.status}>{run.status}</Badge>
                        <div>
                          <Muted>Started {formatDate(run.started_at)}</Muted>
                          <Muted>
                            Actor run: {run.apify_actor_run_id || "manual import"}
                          </Muted>
                          {run.error_message ? (
                            <Muted>{run.error_message}</Muted>
                          ) : null}
                        </div>
                        {run.apify_actor_run_id && run.status !== "imported" ? (
                          <Button
                            disabled={!!busy}
                            onClick={() =>
                              this.perform(
                                `sync-${run.id}`,
                                () =>
                                  api.place_ingestion.runs.sync({ id: run.id }),
                                "Run synchronized."
                              )
                            }
                          >
                            Sync
                          </Button>
                        ) : (
                          <Muted>
                            {(run.result_summary &&
                              run.result_summary.received) ||
                              0}{" "}
                            items
                          </Muted>
                        )}
                      </RunItem>
                    ))}
                  </RunList>
                )}
              </Card>
            </React.Fragment>
          ) : (
            <Card>
              <Empty>Add a testing city to begin.</Empty>
            </Card>
          )}
        </Stack>
      </Page>
    );
  }
}

export default CityIngestion;
